import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Trades } from '@paramount-prestart/shared'
import { getMyTradies } from 'services/clients.svc'
import { PageTitle } from 'context/PageTitleContext'
import { GroupIcon } from 'components/Icons'
import TradeGroup from './TradeGroup'
import './MyTradies.scss'

const buildGroups = (tradies, activeFilters) => {
  const groups = {}
  tradies.forEach((t) => {
    const tradieTrades = t.trades?.length ? t.trades : ['other']
    tradieTrades.forEach((tradeVal) => {
      if (activeFilters.length && !activeFilters.includes(tradeVal)) return
      if (!groups[tradeVal]) groups[tradeVal] = []
      groups[tradeVal].push(t)
    })
  })
  return groups
}

const getAvailableTrades = (tradies) => {
  const present = new Set(tradies.flatMap((t) => t.trades ?? []))
  return Trades.TRADES.filter((t) => present.has(t.value))
}

export default function MyTradies() {
  const { companyId } = useParams()
  const [activeFilters, setActiveFilters] = useState([])
  const [collapsedGroups, setCollapsedGroups] = useState(new Set())

  const toggleGroup = (tradeVal) =>
    setCollapsedGroups((prev) => {
      const next = new Set(prev)
      next.has(tradeVal) ? next.delete(tradeVal) : next.add(tradeVal)
      return next
    })

  const { data: tradies = [], isLoading } = useQuery(
    ['myTradies', companyId],
    () => getMyTradies(companyId),
  )

  const toggleFilter = (tradeVal) =>
    setActiveFilters((prev) =>
      prev.includes(tradeVal) ? prev.filter((v) => v !== tradeVal) : [...prev, tradeVal],
    )

  const availableTrades = getAvailableTrades(tradies)
  const groups = buildGroups(tradies, activeFilters)
  const groupEntries = Object.entries(groups)

  if (isLoading) return <article aria-busy="true">Loading, Please Wait</article>

  return (
    <div className="my-tradies-container">
      <PageTitle title="My Tradies" />

      <div className="my-tradies-header">
        <h2>
          <GroupIcon fill="var(--font-color)" />
          My Tradies
          {tradies.length > 0 && <span className="total-count">{tradies.length}</span>}
        </h2>
      </div>

      <div className="trade-filters">
        <button
          className="clear-filters"
          onClick={() => setActiveFilters([])}
          disabled={activeFilters.length === 0}
        >
          Clear filters
        </button>
        {availableTrades.map((t) => (
          <button
            key={t.value}
            className={`filter-chip${activeFilters.includes(t.value) ? ' filter-chip--active' : ''}`}
            onClick={() => toggleFilter(t.value)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tradies.length === 0 ? (
        <p className="no-results">No tradies have checked in to your sites yet</p>
      ) : (
        groupEntries.map(([tradeVal, groupTradies]) => (
          <TradeGroup
            key={tradeVal}
            label={Trades.tradeLabel(tradeVal)}
            tradies={groupTradies}
            open={!collapsedGroups.has(tradeVal)}
            onToggle={() => toggleGroup(tradeVal)}
          />
        ))
      )}
    </div>
  )
}
